/**
 * Orders from techforce_portal API.
 */

import { API_BASE } from '../config/apiBase';
import { AttachmentVoidFields, patchAttachmentVoid } from './attachmentVoid';

export interface OrderRow {
  id: number;
  order_number: string;
  company_name: string;
  status: string;
  category: string;
  notes?: string | null;
  /** Sales checklist: string values for line items, booleans for checkbox items */
  sales_checklist_state?: Record<string, string | boolean> | null;
  employee_id: number | null;
  employee_name?: string | null;
  /** Everyone assigned to the order, primary (employee_name) first. */
  assignee_names?: string[];
  tracking_number?: string | null;
  estimated_delivery_date?: string | null;
  shipping_address?: string | null;
  deliver_to?: string | null;
  installation_appointment_time?: string | null;
  installation_appointment_id?: number | null;
  site_location?: string | null;
  last_contact_date?: string | null;
  installation_employee_name?: string | null;
  /** Order lifecycle: voided orders move to the "Voided Orders" table. */
  is_voided?: boolean;
  void_reason?: string | null;
  voided_at?: string | null;
  /** Pilot term: length in days; start/end are calendar dates (YYYY-MM-DD). */
  pilot_term_days?: number | null;
  pilot_term_start_date?: string | null;
  pilot_term_end_date?: string | null;
  created_at: string;
  updated_at: string;
}

/** Names of everyone assigned to the order, primary first. */
export function orderAssigneeNames(order: Pick<OrderRow, 'assignee_names' | 'employee_name'>): string[] {
  if (order.assignee_names && order.assignee_names.length > 0) return order.assignee_names;
  return order.employee_name ? [order.employee_name] : [];
}

export async function fetchOrders(): Promise<OrderRow[]> {
  const res = await fetch(`${API_BASE}/api/orders`);
  if (!res.ok) throw new Error('Failed to fetch orders');
  const data = await res.json();
  return data.orders || [];
}

export interface OrderInstallationActivityItem {
  id: number;
  business_name: string;
  when: string | null;
  status: string;
}

export interface OrderInstallationActivity {
  inspections: OrderInstallationActivityItem[];
  installations: OrderInstallationActivityItem[];
  signoffs: OrderInstallationActivityItem[];
}

/** Site inspections, installation appointments, and customer sign-offs tied to an order. */
export async function fetchOrderInstallationActivity(orderNumber: string): Promise<OrderInstallationActivity> {
  const res = await fetch(`${API_BASE}/api/orders/${encodeURIComponent(orderNumber)}/installation-activity`);
  if (!res.ok) throw new Error('Failed to fetch order appointments');
  return res.json();
}

/** Fetch orders assigned to the given user (for Tasks Board To-Do / In Progress). */
export async function fetchOrdersForUser(userId: number): Promise<OrderRow[]> {
  const res = await fetch(`${API_BASE}/api/orders?assigned_to_user=${encodeURIComponent(userId)}`);
  if (!res.ok) throw new Error('Failed to fetch orders');
  const data = await res.json();
  return data.orders || [];
}

export async function fetchOrderByOrderNumber(orderNumber: string): Promise<OrderRow | null> {
  const res = await fetch(`${API_BASE}/api/orders/${encodeURIComponent(orderNumber)}`);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error('Failed to fetch order');
  return res.json();
}

export interface CreateOrderPayload {
  company_name: string;
  employee_name?: string;
  user_id?: number;
}

export async function createOrder(payload: CreateOrderPayload): Promise<OrderRow> {
  const res = await fetch(`${API_BASE}/api/orders`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to create order');
  return data;
}

export interface UpdateOrderPayload {
  /** Everyone assigned to the order, primary first; replaces the current list. */
  employee_names?: string[];
  employee_name?: string | null;
  user_id?: number | null;
  category?: string;
  status?: string;
  notes?: string | null;
  last_contact_date?: string | null;
  tracking_number?: string | null;
  estimated_delivery_date?: string | null;
  shipping_address?: string | null;
  deliver_to?: string | null;
  installation_appointment_time?: string | null;
  installation_employee_name?: string | null;
  site_location?: string | null;
  /** Used for dashboard activity log (who performed the update) */
  acting_user?: string | null;
  sales_checklist_state?: Record<string, string | boolean> | null;
  is_voided?: boolean;
  void_reason?: string | null;
}

export async function updateOrder(orderNumber: string, payload: UpdateOrderPayload): Promise<OrderRow> {
  const res = await fetch(`${API_BASE}/api/orders/${encodeURIComponent(orderNumber)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || 'Failed to update order');
  return data as OrderRow;
}

export async function deleteOrder(orderNumber: string): Promise<void> {
  const res = await fetch(`${API_BASE}/api/orders/${encodeURIComponent(orderNumber)}`, { method: 'DELETE' });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || 'Failed to delete order');
}

/** Void an order (requires a reason). The order moves to the Voided Orders table. */
export async function voidOrder(orderNumber: string, reason: string, actingUser?: string): Promise<OrderRow> {
  return updateOrder(orderNumber, { is_voided: true, void_reason: reason, acting_user: actingUser ?? null });
}

/** Restore a voided order (clears the void reason). */
export async function unvoidOrder(orderNumber: string, actingUser?: string): Promise<OrderRow> {
  return updateOrder(orderNumber, { is_voided: false, acting_user: actingUser ?? null });
}

export type OrderPilotTerm = Required<Pick<OrderRow, 'pilot_term_days' | 'pilot_term_start_date' | 'pilot_term_end_date'>>;

/** Set the term of a Pilot order. Without a start date (YYYY-MM-DD) the term keeps its start, or starts today. */
export async function setOrderPilotTerm(orderNumber: string, days: number, startDate?: string): Promise<OrderPilotTerm> {
  const res = await fetch(`${API_BASE}/api/orders/${encodeURIComponent(orderNumber)}/pilot-term`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ days, start_date: startDate }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || 'Failed to set the term period');
  return data as OrderPilotTerm;
}

// --- Sales checklist section photos (sections 1, 2, 5, 7, 8) ---
export interface OrderSalesChecklistAttachmentRow extends AttachmentVoidFields {
  id: number;
  order_number: string;
  section_id: string;
  original_file_name: string;
  uploaded_by_user_id: number | null;
  uploaded_by: string | null;
  created_at: string;
}

export async function fetchOrderSalesChecklistAttachments(
  orderNumber: string
): Promise<OrderSalesChecklistAttachmentRow[]> {
  const res = await fetch(
    `${API_BASE}/api/orders/${encodeURIComponent(orderNumber)}/sales-checklist-attachments`
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || 'Failed to fetch checklist photos');
  return (data as { attachments?: OrderSalesChecklistAttachmentRow[] }).attachments || [];
}

export async function uploadOrderSalesChecklistAttachment(
  orderNumber: string,
  sectionId: string,
  file: File,
  userId?: number | null
): Promise<OrderSalesChecklistAttachmentRow> {
  const form = new FormData();
  form.append('file', file);
  form.append('section_id', sectionId);
  if (userId != null && userId > 0) form.append('user_id', String(userId));
  const res = await fetch(
    `${API_BASE}/api/orders/${encodeURIComponent(orderNumber)}/sales-checklist-attachments`,
    { method: 'POST', body: form }
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || 'Failed to upload photo');
  return data as OrderSalesChecklistAttachmentRow;
}

export function orderSalesChecklistAttachmentDownloadUrl(
  orderNumber: string,
  attachmentId: number
): string {
  return `${API_BASE}/api/orders/${encodeURIComponent(orderNumber)}/sales-checklist-attachments/${attachmentId}/download`;
}

function orderSalesChecklistAttachmentUrl(orderNumber: string, attachmentId: number): string {
  return `${API_BASE}/api/orders/${encodeURIComponent(orderNumber)}/sales-checklist-attachments/${attachmentId}`;
}

/** Sets a photo aside with a reason. The photo stays on the order and can be restored. */
export function voidOrderSalesChecklistAttachment(
  orderNumber: string,
  attachmentId: number,
  reason: string,
  userId?: number | null
): Promise<OrderSalesChecklistAttachmentRow> {
  return patchAttachmentVoid(orderSalesChecklistAttachmentUrl(orderNumber, attachmentId), reason, userId);
}

export function unvoidOrderSalesChecklistAttachment(
  orderNumber: string,
  attachmentId: number,
  userId?: number | null
): Promise<OrderSalesChecklistAttachmentRow> {
  return patchAttachmentVoid(orderSalesChecklistAttachmentUrl(orderNumber, attachmentId), null, userId);
}

// --- Activity log (one per order, persisted) ---
export interface ActivityLogEntryRow {
  id: number;
  order_number: string;
  timestamp: string;
  action: string;
  user: string;
}

export async function fetchOrderActivityLog(orderNumber: string): Promise<ActivityLogEntryRow[]> {
  const res = await fetch(`${API_BASE}/api/orders/${encodeURIComponent(orderNumber)}/activity-log`);
  if (!res.ok) throw new Error('Failed to fetch activity log');
  const data = await res.json();
  return data.entries || [];
}

export async function addOrderActivityLog(
  orderNumber: string,
  payload: { action: string; user?: string }
): Promise<ActivityLogEntryRow> {
  const res = await fetch(`${API_BASE}/api/orders/${encodeURIComponent(orderNumber)}/activity-log`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: payload.action, user: payload.user ?? 'System' }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to add activity log entry');
  return data;
}

// --- Invoices for an order ---
export interface OrderInvoiceRow {
  id: number;
  order_number: string;
  invoice_number: string | null;
  amount: string | null;
  status: string;
  created_at: string;
  updated_at: string;
  has_pdf: boolean;
}

export async function fetchOrderInvoices(orderNumber: string): Promise<OrderInvoiceRow[]> {
  const res = await fetch(`${API_BASE}/api/orders/${encodeURIComponent(orderNumber)}/invoices`);
  if (!res.ok) throw new Error('Failed to fetch invoices');
  const data = await res.json();
  return data.invoices || [];
}

export async function createInvoiceWithPdf(
  orderNumber: string,
  payload: { pdfBase64: string; invoice_number?: string; amount?: number }
): Promise<OrderInvoiceRow & { has_pdf: boolean }> {
  const res = await fetch(`${API_BASE}/api/orders/${encodeURIComponent(orderNumber)}/invoices`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to create invoice');
  return data;
}

export interface InvoiceConfirmationPayload {
  companyName: string;
  billingEntity?: string;
  billingAddress: string;
  shippingAddress: string;
  products: Array<{ productName: string; serialNumber?: string; quantity?: number }>;
  invoiceDate: string;
  dueDate: string;
  terms: string;
  userEmail?: string;
}

/** Resolves to the address the confirmation was sent to (the bookkeeper on file). */
export async function sendInvoiceConfirmation(
  orderNumber: string,
  payload: InvoiceConfirmationPayload
): Promise<string> {
  const res = await fetch(`${API_BASE}/api/orders/${encodeURIComponent(orderNumber)}/invoice-email`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || 'Failed to send invoice email');
  return (data as { recipient?: string }).recipient || '';
}

export interface SendContractEmailPayload {
  recipients: string[];
  subject: string;
  body: string;
  /** Attach the TechForce Certificate of Insurance PDF. Defaults to true server-side. */
  attachCoi?: boolean;
  /** Attach the TechForce W-9 PDF. Defaults to true server-side. */
  attachW9?: boolean;
  /** Include a link to the Elevator Initial Survey. Defaults to true server-side. */
  includeElevatorSurvey?: boolean;
  /** Extra files the sender chose to include with this email. */
  files?: File[];
}

/** Send the contract signing link to the client (and optional internal CCs). The server pulls the
 *  contract_id from the URL and reconstructs the canonical link using APP_URL. */
export async function sendContractEmail(
  contractId: string,
  payload: SendContractEmailPayload
): Promise<{ recipients: string[] }> {
  const form = new FormData();
  form.append('recipients', JSON.stringify(payload.recipients));
  form.append('subject', payload.subject);
  form.append('body', payload.body);
  form.append('attachCoi', payload.attachCoi === false ? 'false' : 'true');
  form.append('attachW9', payload.attachW9 === false ? 'false' : 'true');
  form.append('includeElevatorSurvey', payload.includeElevatorSurvey === false ? 'false' : 'true');
  for (const file of payload.files ?? []) {
    form.append('files', file, file.name);
  }
  const res = await fetch(`${API_BASE}/api/contracts/${encodeURIComponent(contractId)}/send-email`, {
    method: 'POST',
    body: form,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || 'Failed to send contract email');
  return data as { recipients: string[] };
}

/** Send the Elevator Initial Survey link to the client on its own (independent of the contract email). */
export async function sendElevatorSurvey(
  orderNumber: string,
  payload: { recipients: string[]; user_id?: number }
): Promise<{ recipients: string[]; link: string }> {
  const res = await fetch(`${API_BASE}/api/orders/${encodeURIComponent(orderNumber)}/send-elevator-survey`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || 'Failed to send survey');
  return data as { recipients: string[]; link: string };
}

/** Sales by product (TIM-E Bot, BIM-E) from completed orders, per period */
export interface SalesProductCountsPeriod {
  productData: { product: string; sales: number }[];
  totalData: { date: string; total: number }[];
}

export type SalesProductCounts = Record<'1month' | '3months' | '6months' | '1year', SalesProductCountsPeriod>;

export async function fetchSalesProductCounts(): Promise<SalesProductCounts> {
  const res = await fetch(`${API_BASE}/api/sales/product-counts`);
  if (!res.ok) throw new Error('Failed to fetch sales by product');
  const data = await res.json();
  return data as SalesProductCounts;
}

/** Site-wide activity log entry (dashboard) */
export interface SiteActivityEntry {
  id: number;
  action: string;
  user: string;
  created_at: string;
}

export async function fetchSiteActivity(limit = 50): Promise<SiteActivityEntry[]> {
  const res = await fetch(`${API_BASE}/api/activity?limit=${limit}`);
  if (!res.ok) throw new Error('Failed to fetch activity');
  const data = await res.json();
  return (data.entries || []) as SiteActivityEntry[];
}
